import { CellContext } from '@tanstack/react-table';
import { getTableMeta, UnallocatedCampaignsRow } from '../../helpers';
import { Button } from '@round/whitelabel-ui';
import { ProgressBar, SkeletonizedValue } from '@round/ui-kit';
import { toDecimalPoint } from '@round/utils';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { asMoneyInKNotation, asMoneyWithoutZeros, getExponent } from 'helpers';
import styles from './ManageCell.module.css';
import cn from 'classnames';
import AllocationBadge from './AllocationBadge/AllocationBadge';
import { Tooltip } from '@round/whitelabel-ui';
import PurchaseOrderBadges from 'Modules/Plans/Finance/PurchaseOrders/components/PurchaseOrderBadges/PurchaseOrderBadges';

type Context = CellContext<UnallocatedCampaignsRow, any>;

const ManageCell = ({ row: { original }, table }: Context) => {
    const { isLoading: isFetching, onAllocate, getArePurchaseOrdersLoading } = getTableMeta(table);
    const isLoading = isFetching || Object.keys(original).length === 0;

    const { getCurrencyById } = useCurrencies();

    const percentAllocated = original?.cost ? (original?.purchase_orders_allocated_amount / original?.cost) * 100 : 100;

    const currency = getCurrencyById(original?.currency_id);
    const allocated = original?.purchase_orders_allocated_amount;

    return (
        <div className={styles.container}>
            <div className={styles.progressContainer}>
                <SkeletonizedValue isInitialized={!isLoading} height="1.8rem" width="100%">
                    <div className={cn(styles.row, styles.amounts)}>
                        <Tooltip.Root>
                            {(anchor, setAnchor) => (
                                <>
                                    <span ref={setAnchor}>
                                        <b>{asMoneyInKNotation({ amount: allocated, currency, threshold: 5 })}</b>
                                        &nbsp;/&nbsp;
                                        {asMoneyInKNotation({ amount: original?.cost, currency, threshold: 5 })}
                                    </span>

                                    <Tooltip
                                        anchorElement={anchor}
                                        disabled={
                                            !original?.cost ||
                                            (getExponent(allocated) <= 5 && getExponent(original?.cost) <= 5)
                                        }
                                    >
                                        <Tooltip.Body>
                                            <span>
                                                <b>{asMoneyWithoutZeros(allocated, currency)}</b>
                                                &nbsp;/&nbsp;
                                                {asMoneyWithoutZeros(original?.cost, currency)}
                                            </span>
                                        </Tooltip.Body>
                                    </Tooltip>
                                </>
                            )}
                        </Tooltip.Root>

                        <SkeletonizedValue isInitialized={!isLoading} height="1.125rem" width="7rem">
                            {typeof original?.cost === 'number' && (
                                <AllocationBadge
                                    originalAmount={original.cost}
                                    allocatedAmount={allocated}
                                    currency={currency ?? null}
                                />
                            )}
                        </SkeletonizedValue>

                        <p className={styles.percentAllocated}>{toDecimalPoint(percentAllocated, 2)}%</p>
                    </div>

                    <ProgressBar
                        barClassName={cn(styles.bar, { [styles.statusGood]: percentAllocated === 100 })}
                        trackClassName={styles.track}
                        progress={Math.min(percentAllocated, 100)}
                    />
                </SkeletonizedValue>
            </div>

            <div className={styles.row}>
                <PurchaseOrderBadges
                    purchaseOrders={original.purchaseOrders ?? []}
                    isLoading={isLoading || getArePurchaseOrdersLoading?.(original)}
                    noDataLabel="No purchase orders"
                />

                <SkeletonizedValue isInitialized={!isLoading} height="1.125rem" width="7rem">
                    <Button
                        appearance="primary"
                        className={styles.manageButton}
                        type="button"
                        onClick={() => onAllocate(original)}
                    >
                        Manage
                    </Button>
                </SkeletonizedValue>
            </div>
        </div>
    );
};

export default ManageCell;
